import React, {FC, lazy, ReactElement, Suspense, useEffect, useState} from 'react';
import NavBar from './components/NavBar';
import {Route, Routes} from 'react-router-dom';
import Loading from './components/Loading';
import {Grid} from '@mui/material';
import logo_white from './logo_white.png';
import './MainApp.scss';
import './components/broadcast/BroadcastDialog.scss';
import SnackbarToast from './components/errors/SnackbarToast';
import {LoginInfo} from './contexts/Configuration';
import {saveUserInfo} from './reducers/accessReducer';
import {getBalance} from './reducers/billingReducer';
import {useAppDispatch} from './hooks/storeHooks';
import {useMsal} from '@azure/msal-react';
import useExecWithToken from './utils/FetchUtils';
import {jwtDecode} from 'jwt-decode';
import {IdTokenPayload} from './utils/IdTokenPayload';
import TosDialog, {getTosAccepted} from './components/tos/Tos';
import {SessionProvider} from './contexts/SessionContextProvider';

const Loadable = (Component: any) => (props: JSX.IntrinsicAttributes) => (
    <Suspense fallback={<Loading/>}>
        <Component {...props}/>
    </Suspense>
)

const Billing = Loadable(lazy(() => import ("./pages/Billing")));
const Transcription = Loadable(lazy(() => import ("./pages/Transcription")));
const BackOffice = Loadable(lazy(() => import ("./pages/BackOffice")));
const Help = Loadable(lazy(() => import ("./pages/Help")));
const ContactUs = Loadable(lazy(() => import ("./pages/ContactUs")));
const TwoWay = Loadable(lazy(() => import ("./pages/TwoWay")));
const Glossary = Loadable(lazy(() => import ("./pages/Glossary")));


const OperatorApp: FC<OperatorAppProps> = ({}): ReactElement => {

    const [loading, setLoading] = useState<boolean>(true)
    const [tosAccepted, setTosAccepted] = useState<boolean>(false)

    const dispatch = useAppDispatch();
    const {accounts} = useMsal();

    const execWithToken = useExecWithToken();


    useEffect(() => {
        execWithToken((token) => {
            let activeAccount = accounts[0];
            const decode = jwtDecode<IdTokenPayload>(token.replace("Bearer ", ""));
            const roles = decode.roles;
            const loginInfo: LoginInfo = {account: activeAccount, token: token, roles: roles};

            dispatch(saveUserInfo(loginInfo));

            getTosAccepted(token, activeAccount.username)
                .then(accepted => {
                    setTosAccepted(accepted);
                    setLoading(false);
                });
        });
    }, [accounts[0]?.homeAccountId]);

    useEffect(() => {
        if (tosAccepted) {
            execWithToken((token) => {
                dispatch(getBalance({token: token}));
            });
        }
    }, [tosAccepted]);

    if (loading) {
        return (
            <div className="loading-screen">
                <img src={logo_white} alt="Company Logo" className="logo"/>
            </div>
        );
    }

    if (!tosAccepted) {
        return <TosDialog onTosAccepted={accepted => setTosAccepted(accepted)}/>
    }

    return (
        <Grid container spacing={0} className='main-container'>
            {/*NavBar*/}
            <Grid item xs={1} md={2} className='nav-bar-component'>
                <NavBar/>
            </Grid>
            {/*Body*/}
            <Grid item xs={11} md={10} className='content-container'>
                <div className='route-container'>
                    <Routes>
                        {/* SessionProvider is required here: Controler.tsx reads useEnersoundSessionContext()
                            to derive the TTS AudioNode for the output level meter. Without it the meter
                            silently stays at zero on the main transcription view. */}
                        <Route path='/' element={<SessionProvider><SnackbarToast><Transcription/></SnackbarToast></SessionProvider>}/>
                        <Route path='/twoWay' element={<SessionProvider><SnackbarToast><TwoWay/></SnackbarToast></SessionProvider>}/>
                        <Route path='/billing' element={<SnackbarToast><Billing/></SnackbarToast>}/>
                        <Route path='/backoffice' element={<SnackbarToast><BackOffice/></SnackbarToast>}/>
                        <Route path='/help' element={<SnackbarToast><Help/></SnackbarToast>}/>
                        <Route path='/contactUs' element={<SnackbarToast><ContactUs/></SnackbarToast>}/>
                        <Route path='/glossaries' element={<SnackbarToast><Glossary/></SnackbarToast>}/>
                    </Routes>
                </div>
            </Grid>
        </Grid>
    )
}


type OperatorAppProps = {}

export default OperatorApp;
